"use client";

import * as Sentry from "@sentry/nextjs";

type DiagnosticLevel = "info" | "warn" | "error";
type DiagnosticDetails = Record<string, unknown>;

const STORAGE_KEY = "locarora_native_flow_logs";
export const NATIVE_FLOW_LOG_EVENT = "locarora:native-flow-log";
const MAX_ENTRIES = 120;

const REDACTED_KEYS = new Set([
  "address",
  "bookerInfo",
  "buyerEmail",
  "buyerName",
  "buyerPhone",
  "customer",
  "email",
  "fullName",
  "guestInfo",
  "name",
  "phone",
  "phoneNumber",
  "reservationData",
]);

function sanitizeUrl(value: string): string {
  try {
    const url = new URL(value);
    const safeParams = new URLSearchParams();
    for (const key of [
      "paymentId",
      "orderGroupId",
      "type",
      "code",
      "pgCode",
      "transactionType",
    ]) {
      const param = url.searchParams.get(key);
      if (param) safeParams.set(key, param);
    }
    const query = safeParams.toString();
    return `${url.origin}${url.pathname}${query ? `?${query}` : ""}`;
  } catch {
    return value;
  }
}

function sanitizeValue(key: string, value: unknown): unknown {
  if (REDACTED_KEYS.has(key)) return "[redacted]";
  if (value == null) return value;
  if (typeof value === "string") {
    if (/^https?:\/\//.test(value)) return sanitizeUrl(value);
    if (value.includes("@")) return "[redacted-email]";
    if (/^\+?\d[\d\s-]{6,}$/.test(value)) return "[redacted-phone]";
    return value.length > 500 ? `${value.slice(0, 500)}...` : value;
  }
  if (Array.isArray(value)) {
    return value.slice(0, 20).map((item) => sanitizeValue(key, item));
  }
  if (typeof value === "object") {
    const input = value as Record<string, unknown>;
    return Object.fromEntries(
      Object.entries(input).map(([childKey, childValue]) => [
        childKey,
        sanitizeValue(childKey, childValue),
      ]),
    );
  }
  return value;
}

function sanitizeDetails(details: DiagnosticDetails = {}): DiagnosticDetails {
  return Object.fromEntries(
    Object.entries(details).map(([key, value]) => [
      key,
      sanitizeValue(key, value),
    ]),
  );
}

function appendStoredLog(entry: DiagnosticDetails) {
  if (typeof window === "undefined") return;
  try {
    const previous = JSON.parse(
      sessionStorage.getItem(STORAGE_KEY) || "[]",
    ) as DiagnosticDetails[];
    previous.push(entry);
    sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify(previous.slice(-MAX_ENTRIES)),
    );
    window.dispatchEvent(
      new CustomEvent(NATIVE_FLOW_LOG_EVENT, { detail: entry }),
    );
  } catch {
    // Diagnostics must never break checkout/auth flows.
  }
}

export function logNativeFlow(
  scope: string,
  event: string,
  details: DiagnosticDetails = {},
  level: DiagnosticLevel = "info",
) {
  const entry = {
    ts: new Date().toISOString(),
    scope,
    event,
    details: sanitizeDetails(details),
    path: typeof window === "undefined" ? undefined : window.location.pathname,
  };

  appendStoredLog(entry);

  const method = level === "error" ? console.error : level === "warn" ? console.warn : console.info;
  method(`[locarora:${scope}] ${event}`, entry);
  const sentryLevel = level === "warn" ? "warning" : level;

  Sentry.addBreadcrumb({
    category: "native-flow",
    level: sentryLevel,
    message: `${scope}:${event}`,
    data: entry.details,
  });

  if (level === "warn" || level === "error") {
    Sentry.captureMessage(`Native flow ${scope}:${event}`, {
      level: sentryLevel,
      tags: { scope, nativeFlowEvent: event },
      extra: entry,
    });
  }
}

export function readNativeFlowLogs(): DiagnosticDetails[] {
  if (typeof window === "undefined") return [];
  try {
    return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || "[]") as DiagnosticDetails[];
  } catch {
    return [];
  }
}
